// Model definitions for CourtMate Web
// Created by Taulant Geci

export enum SkillLevel {
  NOT_SET = 'not_set',
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate',
  ADVANCED = 'advanced'
}

export interface Player {
  id: string;
  name: string;
  skillLevel: SkillLevel;
}

export interface Court {
  id: string;
  number: number;
  label: string;
}

export interface Match {
  id: string;
  players: Player[];
  court: Court;
  isDoubles: boolean;
}

export interface PlayerGroup {
  id: string;
  name: string;
  playerIDs: Set<string>;
}

export interface MatchHistory {
  id: string;
  matches: Match[];
  timestamp: Date;
  unmatchedPlayers: Player[];
  groupId?: string; // New: Track which group this generation was for
  groupName?: string; // New: Store group name for easy display
}

export interface MatchResult {
  id: string;
  matchId: string;
  winnerIds: string[];
  loserIds: string[];
  winnerScore: number;
  loserScore: number;
  timestamp: Date;
  groupId?: string; // New: Track which group this result belongs to
  groupName?: string; // New: Store group name for easy display
}

// iOS-style Match Generation Enums and Interfaces
export enum GenerationMode {
  BALANCED = 'balanced',
  DOUBLES_ONLY = 'doublesOnly',
  SINGLES_PREFERRED = 'singlesPreferred'
}

export enum MatchType {
  SINGLES = 'singles',
  DOUBLES = 'doubles',
  PAUSE = 'pause',
  NONE = 'none'
}

// iOS-style Match Configuration
export interface MatchConfiguration {
  singlesPerRound: number;
  doublesPerRound: number;
  isValid: boolean;
  totalPlayerSlots: number;
}

// iOS-style Player Quota System
export interface PlayerQuota {
  targetTotalGames: number;
  targetSingles: number;
  targetPauses: number;
  playedGames: number;
  playedSingles: number;
  pausesTaken: number;
  lastRoundAction: MatchType;
  roundHistory: Map<number, MatchType>;
  remainingGames: number;
  remainingSingles: number;
  remainingPauses: number;
  mustPauseThisRound: boolean;
  mustPlayThisRound: boolean;
}

// iOS-style Player Match Statistics with Round History
export interface PlayerMatchStats {
  singlesCount: number;
  doublesCount: number;
  pauseCount: number;
  partnerHistory: Map<string, number>;
  opponentHistory: Map<string, number>;
  lastMatchType: MatchType;
  lastRoundMatchType: MatchType;
  roundHistory: Map<number, MatchType>;
  pauseHistory: Map<number, boolean>;
  totalMatches: number;
}

// iOS-style Round Result
export interface RoundResult {
  roundNumber: number;
  matches: Match[];
  unmatchedPlayers: Player[];
  matchConfiguration: MatchConfiguration;
}

export interface PlayerStatistics {
  playerId: string;
  playerName: string;
  wins: number;
  losses: number;
  totalMatches: number;
  winRate: number;
  // New: Enhanced tracking like iOS app
  singlesCount: number;
  doublesCount: number;
  pauseCount: number;
  partnerHistory: Map<string, number>; // playerId -> count of times played as partner
  opponentHistory: Map<string, number>; // playerId -> count of times played as opponent
}

// New interfaces for iOS-style fairness tracking
export interface PlayerStats {
  partnerships: Map<string, number>; // playerId -> count of times played together/against
  pauseCount: number;
  lastMatchType: MatchType;
  // New: Enhanced tracking
  singlesCount: number;
  doublesCount: number;
  partnerHistory: Map<string, number>; // Partner relationships (doubles teammates)
  opponentHistory: Map<string, number>; // Opponent relationships (all match types)
  totalMatches: number;
}

export interface MatchGenerationResult {
  matches: Match[];
  unmatchedPlayers: Player[];
}

// New: Filter interfaces for statistics
export interface StatisticsFilter {
  groupId?: string;
  dateFrom?: Date;
  dateTo?: Date;
  showAllTime: boolean;
}

export interface FilteredStatistics {
  statistics: PlayerStatistics[];
  totalMatches: number;
  dateRange: string;
  groupName?: string;
}

export interface AppState {
  // Player management
  activePlayers: Player[];
  allPlayers: Player[];
  playerGroups: PlayerGroup[];
  
  // Court and match management
  courts: Court[];
  generatedMatches: Match[];
  unmatchedPlayers: Player[];
  selectedCourtCount: number;
  
  // History and statistics
  matchHistory: MatchHistory[];
  matchResults: MatchResult[];
  playerStatistics: PlayerStatistics[];
  
  // New: Fairness tracking for iOS-style algorithm
  playerStats: Map<string, PlayerStats>;
  playerMatchStats: Map<string, PlayerMatchStats>; // iOS-style detailed stats
  
  // New: Round tracking
  currentRound: number;
  
  // New: Statistics filtering
  statisticsFilter: StatisticsFilter;
  
  // UI state
  isLoading: boolean;
  selectedGroupID?: string;
  newGroupName: string;
}

export enum SortOrder {
  WINS = 'wins',
  LOSSES = 'losses',
  WIN_RATE = 'winRate',
  TOTAL_MATCHES = 'totalMatches',
  NAME = 'name'
}

// New: Partnership matrix data structure
export interface PartnershipMatrixData {
  playerId1: string;
  playerId2: string;
  partnershipCount: number; // Times they were partners (doubles)
  opponentCount: number; // Times they were opponents (singles + doubles)
  totalInteractions: number; // Total times they played together
}

// New: Enhanced statistics for display
export interface DetailedPlayerStatistics extends PlayerStatistics {
  partnershipMatrix: PartnershipMatrixData[];
  lastMatchType: MatchType;
} 